/*
 * PE FAQ Tabs
 *
 * Drives sections/pe-faq-tabs.liquid. Two small jobs, nothing else:
 *
 *   1. Tab filtering. The panels ship visible and the script hides the inactive
 *      ones on upgrade, so with this file blocked every question is still
 *      reachable — the section degrades to one long list rather than trapping
 *      content behind tabs that cannot be clicked.
 *   2. Optional exclusive open, when the section sets data-single-open.
 *
 * The rows themselves are <details>/<summary> and need no script at all.
 *
 * Keyboard handling is the ARIA tabs pattern with a roving tabindex, matching
 * assets/product-tabs.js: exactly one tab is in the page tab order, so Tab
 * reaches the row and then moves past it, and the arrow keys move within it.
 *
 * No globals, no libraries. Listeners are bound per instance and torn down in
 * disconnectedCallback so theme-editor section reloads cannot double-bind.
 */

if (!customElements.get('pe-faq-tabs')) {
  customElements.define(
    'pe-faq-tabs',
    class PeFaqTabs extends HTMLElement {
      #abort = null;

      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('.pe-faq__panel'));
        this.rows = Array.from(this.querySelectorAll('.pe-faq__row'));

        this.#abort = new AbortController();
        const { signal } = this.#abort;

        // Liquid marks every panel after the first with data-pe-faq-inactive.
        // Acting on it here rather than writing `hidden` server-side is what
        // keeps the no-JS fallback honest.
        this.panels.forEach((panel) => {
          if (panel.hasAttribute('data-pe-faq-inactive')) panel.hidden = true;
        });

        // A single category renders no tab row; the accordion still works.
        if (this.tabs.length > 1) {
          this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.select(index), { signal });
            tab.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal });
          });
        }

        if (this.dataset.singleOpen === 'true') {
          this.rows.forEach((row) => {
            // `toggle` fires on open and on close; only an open needs to shut
            // the others, and closing the rest cannot re-enter this handler
            // because setting `open = false` on an already-closed row is a
            // no-op that fires nothing.
            row.addEventListener(
              'toggle',
              () => {
                if (!row.open) return;
                this.rows.forEach((other) => {
                  if (other !== row && other.open) other.open = false;
                });
              },
              { signal }
            );
          });
        }
      }

      disconnectedCallback() {
        this.#abort?.abort();
        this.#abort = null;
      }

      select(index, { focus = false } = {}) {
        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          tab.classList.toggle('pe-faq__tab--active', active);
        });

        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });

        if (focus) this.tabs[index]?.focus();
      }

      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        let target = null;

        switch (event.key) {
          case 'ArrowRight':
            target = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
            target = index === 0 ? last : index - 1;
            break;
          case 'Home':
            target = 0;
            break;
          case 'End':
            target = last;
            break;
          default:
            return;
        }

        // Arrow keys would otherwise scroll the page while the tab row has
        // focus, which reads as the tabs being unresponsive.
        event.preventDefault();
        this.select(target, { focus: true });
      }
    }
  );
}
